/**
 * @copyright (C) 2023, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * HyusnSu Sim (hssim@rsupport.com)
 *
 * @description
 *
 * @created at Thu Mar 23 2023
 **/

import { css } from "@emotion/react";
import type { Theme } from "@emotion/react";
import { ButtonProps, ToolbarButtonProps } from "../Button";
import { StyleType, TOOLBAR_SIZE, VARIANT_PALETTE } from "./palette";

export const buttonStyle = css`
	position: relative;
	display: flex;
	gap: 6px;
	flex-direction: row;
	align-items: center;
	justify-content: center;
	/*
	해당 값으로 인해 좌우 패딩이 정상적으로 동작하지 않아 제거함.
	overflow: hidden;
	*/
	border: 0;

	.circle {
		position: absolute;
		background: #eee;
		width: 100px;
		height: 100px;
		border-radius: 50%;
		transform: translate(-50%, -50%) scale(0);
		animation: scale 0.5s ease-out;
	}

	@keyframes scale {
		to {
			transform: translate(-50%, -50%) scale(3);
			opacity: 0;
		}
	}
`;

export const typeStyle = (
	theme: Theme,
	variant: ButtonProps["variant"],
	filled: ButtonProps["filled"] = "primary",
	fill: keyof Theme["colors"] = "neutral_80_100",
	typoColor?: keyof Colors
) => {
	switch (variant) {
		case "text":
			return variantStyle(theme, VARIANT_PALETTE()[variant], typoColor);
		case "contained":
			return variantStyle(theme, VARIANT_PALETTE(fill)[variant][filled], typoColor);
		case "outlined":
			return variantStyle(theme, VARIANT_PALETTE()[variant], typoColor);
		default:
			throw new Error("variant 가 설정되지 않았습니다.");
	}
};

const variantStyle = (theme: Theme, data: StyleType, typoColor?: keyof Colors) => {
	const borderColor = data.default.border as keyof Colors;

	const backgroundColor = data.default.background as keyof Colors;
	const backgroundHoverColor = data.hover.background as keyof Colors;
	const backgroundFocusedColor = data.focused.background as keyof Colors;
	const pressedBackgroundColor = data.pressed.background as keyof Colors;
	const disabledBackgroundColor = data.disabled.background as keyof Colors;

	const color = data.default.color as keyof Colors;
	const hoverColor = data.hover.color as keyof Colors;
	const focusedColor = data.focused.color as keyof Colors;
	const pressedColor = data.pressed.color as keyof Colors;
	const disabledColor = data.disabled.color as keyof Colors;

	return css`
		background: ${theme.colors[backgroundColor]};
		color: ${theme.colors[color]};

		border: ${borderColor ? "1px solid " + `${theme.colors[borderColor]}` : "0"};

		svg {
			fill: ${theme.colors[color]};
		}

		> .buttonText {
			color: ${typoColor ? typoColor : theme.colors[color]};
		}

		&:hover {
			> .buttonText {
				color: ${typoColor ? typoColor : theme.colors[hoverColor]};
			}

			background: ${theme.colors[backgroundHoverColor]};
			transition: all 0.25s ease-in-out;
			svg {
				fill: ${theme.colors[hoverColor]};
			}
		}

		&:focus-visible {
			background: ${theme.colors[backgroundFocusedColor]};
			svg {
				fill: ${theme.colors[focusedColor]};
			}
		}

		&:active {
			> .buttonText {
				color: ${typoColor ? typoColor : theme.colors[pressedColor]};
			}
			background: ${theme.colors[pressedBackgroundColor]};
			svg {
				fill: ${theme.colors[pressedColor]};
			}
		}

		&:disabled {
			> .buttonText {
				color: ${typoColor ? typoColor : theme.colors[disabledColor]};
			}

			background: ${theme.colors[disabledBackgroundColor]};
			cursor: not-allowed;
			svg {
				fill: ${theme.colors[disabledColor]};
			}

			&:hover {
				transition: none;
			}
		}
	`;
};

export const shapeStyle = (radius: string) => css`
	border-radius: ${radius};
`;

export const sizeStyle = (styles: DesignStuff.Button.SizeStylesProps) => css`
	${styles};
`;

export const cursorStyle = css`
	cursor: pointer;
`;

export const buttonGroupStyle = css`
	display: flex;
	gap: 8px;
`;

export const iconSizeStyle = (styles: DesignStuff.Button.IconSizeStylesProps) => css`
	${styles}
`;

export const toolbarButtonStyle = (
	theme: Theme,
	size: ToolbarButtonProps["size"] = "large",
	active: ToolbarButtonProps["active"]
) => css`
	width: ${TOOLBAR_SIZE[size].width};
	height: ${TOOLBAR_SIZE[size].height};
	background: ${active ? theme.colors.gradient_40_100 : "transparent"};

	&:hover {
		background: ${theme.colors.gradient_40_100};
	}
`;

export const loginButtonStyle = (theme: Theme) => css`
	display: flex;
	gap: 8px;
	width: 288px;
	height: 40px;
	color: ${theme.colors.neutral_70_100};
	background: ${theme.colors.neutral_00_100};
	border: 1px solid ${theme.colors.neutral_60_100};
	border-radius: 8px;
`;

export const reactionButtonStyle = (theme: Theme) => css`
	padding: 0 7px;
	display: flex;
	flex-direction: row;
	gap: 7px;
	height: 24px;
	color: ${theme.colors.neutral_80_100};
	background: ${theme.colors.neutral_00_100};
	border: 1px solid ${theme.colors.neutral_20_100};
	border-radius: 16px;

	transition: all 0.25s ease-in-out;

	&:hover {
		background: ${theme.colors.secondary_80_8};
	}

	&:active {
		p {
			color: ${theme.colors.accent_40_100};
		}
	}

	&:disabled {
		p {
			color: ${theme.colors.neutral_80_32};
		}
	}
`;

export const failedButtonStyle = (theme: Theme, role: "refresh" | "delete") => css`
	width: 24px;
	height: 24px;
	padding: 3px;
	border-radius: 8px;
	background-color: ${role === "delete" ? theme.colors.error_60_100 : theme.colors.accent_40_100};
	cursor: pointer;
`;
